import { apiJson } from '../api';
import { RECENT_SCORED_TOKEN } from '../utils/topTldrSources';
import type {
  AdminArticleScore,
  ArticleScoreEvent,
  QualityScoringPrompt,
  RecentArticlesResponse,
  RescoreJob,
} from '../types';

export async function listQualityScoringPrompts(): Promise<QualityScoringPrompt[]> {
  const data = await apiJson<QualityScoringPrompt[]>('/api/admin/quality-scoring/prompts');
  return Array.isArray(data) ? data : [];
}

export async function getActiveQualityScoringPrompt(): Promise<QualityScoringPrompt> {
  return apiJson<QualityScoringPrompt>('/api/admin/quality-scoring/prompts/active');
}

export async function getQualityScoringPrompt(promptId: string): Promise<QualityScoringPrompt> {
  return apiJson<QualityScoringPrompt>(
    `/api/admin/quality-scoring/prompts/${encodeURIComponent(promptId)}`
  );
}

export async function createQualityScoringPrompt(payload: {
  system_prompt: string;
  user_prompt_template: string;
  model: string | null;
}): Promise<QualityScoringPrompt> {
  return apiJson<QualityScoringPrompt>('/api/admin/quality-scoring/prompts', {
    method: 'POST',
    json: payload,
  });
}

export async function activateQualityScoringPrompt(promptId: string): Promise<QualityScoringPrompt> {
  return apiJson<QualityScoringPrompt>(
    `/api/admin/quality-scoring/prompts/${encodeURIComponent(promptId)}/activate`,
    { method: 'POST' }
  );
}

export async function archiveQualityScoringPrompt(promptId: string, archived: boolean): Promise<QualityScoringPrompt> {
  const action = archived ? 'archive' : 'unarchive';
  return apiJson<QualityScoringPrompt>(
    `/api/admin/quality-scoring/prompts/${encodeURIComponent(promptId)}/${action}`,
    { method: 'POST' }
  );
}

export async function getRecentScoredArticles(
  source: string = RECENT_SCORED_TOKEN,
  excludePromptId?: string,
): Promise<RecentArticlesResponse> {
  let url = `/api/admin/quality-scoring/recent-articles?source=${encodeURIComponent(source)}`;
  if (excludePromptId) {
    url += `&exclude_prompt_id=${encodeURIComponent(excludePromptId)}`;
  }
  return apiJson<RecentArticlesResponse>(url);
}

export async function startRescoreJob(payload: {
  prompt_id: string;
  article_ids: string[];
}): Promise<RescoreJob> {
  return apiJson<RescoreJob>('/api/admin/quality-scoring/rescore-jobs', {
    method: 'POST',
    json: payload,
  });
}

export async function getRescoreJob(jobId: string): Promise<RescoreJob> {
  return apiJson<RescoreJob>(
    `/api/admin/quality-scoring/rescore-jobs/${encodeURIComponent(jobId)}`
  );
}

export async function getArticleScores(articleIds: string[]): Promise<ArticleScoreEvent[]> {
  if (articleIds.length === 0) return [];
  const data = await apiJson<{ scores: ArticleScoreEvent[] }>(
    '/api/admin/quality-scoring/article-scores',
    {
      method: 'POST',
      json: { article_ids: articleIds },
    },
  );
  return data.scores ?? [];
}

export async function setReferenceScore(
  articleId: string,
  score: number | null,
  comment?: string,
): Promise<{ article_id: string; reference_score: number | null; tier: number | null }> {
  return apiJson<{ article_id: string; reference_score: number | null; tier: number | null }>(
    `/api/admin/quality-scoring/articles/${encodeURIComponent(articleId)}/reference-score`,
    {
      method: 'PUT',
      json: { score, comment: comment || undefined },
    },
  );
}

export async function applyRescoreResults(jobId: string): Promise<{ updated: number }> {
  return apiJson<{ updated: number }>(
    `/api/admin/quality-scoring/rescore-jobs/${encodeURIComponent(jobId)}/apply`,
    { method: 'POST' },
  );
}

export async function cancelRescoreJob(jobId: string): Promise<unknown> {
  return apiJson(
    `/api/admin/quality-scoring/rescore-jobs/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  );
}

export async function getAdminArticleScores(articleIds: string[]): Promise<AdminArticleScore[]> {
  if (articleIds.length === 0) return [];
  const data = await apiJson<{ scores: AdminArticleScore[] }>(
    '/api/admin/quality-scoring/admin-scores',
    {
      method: 'POST',
      json: { article_ids: articleIds },
    },
  );
  return data.scores ?? [];
}
